import type { PrescriptionTemplateWithItems } from "@/hooks/usePrescriptionTemplates";
import type {
  ManipulatedComponentInput,
  ManipulatedPayloadInput,
  PrescriptionItemInput,
} from "@/lib/validations/medical-prescription";
import { resolveCalendarUnitForCount } from "@/lib/prescriptions/posology-units";

export type DuplicateResolution = "all" | "unique";

export interface StandardTemplateDuplicateConflict {
  templateId: string;
  templateTitle: string;
  duplicateNames: string[];
}

export interface ManipulatedTemplateDuplicateConflict {
  templateId: string;
  templateTitle: string;
  duplicateNames: string[];
}

const normalizeName = (value: string) =>
  value
    .trim()
    .toLocaleLowerCase()
    .replace(/\s+/g, " ");

export const appendTemplateObservations = (
  currentObservations: string | undefined,
  templateObservations: string | null | undefined,
): string => {
  const current = currentObservations?.trim() ?? "";
  const incoming = templateObservations?.trim() ?? "";

  if (!incoming) return currentObservations ?? "";
  if (!current) return incoming;

  return `${current}\n\n${incoming}`;
};

export const mapStandardTemplateItems = (
  template: PrescriptionTemplateWithItems,
  language = "pt-BR",
): PrescriptionItemInput[] =>
  template.items.map((item) => ({
    medication_id: item.medication_id ?? "",
    medication_name: item.medication_name ?? "",
    medication_concentration: item.medication_concentration ?? "",
    pharmaceutical_form: item.pharmaceutical_form ?? "",
    administration_route: item.administration_route ?? "",
    presentation: item.presentation ?? "",
    posology_mode: item.posology_mode ?? "automatic",
    free_text_posology: item.free_text_posology ?? "",
    dose_amount: item.dose_amount ?? "",
    dose_unit: item.dose_unit ?? "",
    frequency_value: item.frequency_value ?? "",
    frequency_unit: item.frequency_unit
      ? resolveCalendarUnitForCount(item.frequency_unit, item.frequency_value ?? "", language)
      : "",
    duration_value: item.duration_value ?? "",
    duration_unit: item.duration_unit
      ? resolveCalendarUnitForCount(item.duration_unit, item.duration_value ?? "", language)
      : "",
  }));

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

export const mapManipulatedTemplatePayload = (
  template: PrescriptionTemplateWithItems,
): ManipulatedPayloadInput => {
  const payload = isRecord(template.manipulated_payload) ? template.manipulated_payload : {};
  const components = Array.isArray(payload.components) ? payload.components : [];

  return {
    components: components
      .map((component) => {
        const current = isRecord(component) ? component : {};
        return {
          medication_id: typeof current.medication_id === "string" ? current.medication_id : "",
          name: typeof current.name === "string" ? current.name : "",
          concentration: typeof current.concentration === "string" ? current.concentration : "",
          free_text: typeof current.free_text === "string" ? current.free_text : "",
        } satisfies ManipulatedComponentInput;
      })
      .filter((component) => component.name.trim().length > 0 || component.free_text.trim().length > 0),
    pharmaceutical_form:
      typeof payload.pharmaceutical_form === "string" ? payload.pharmaceutical_form : "",
    treatment_days:
      typeof payload.treatment_days === "string" || typeof payload.treatment_days === "number"
        ? payload.treatment_days
        : "",
    total_doses:
      typeof payload.total_doses === "string" || typeof payload.total_doses === "number"
        ? payload.total_doses
        : "",
    route: typeof payload.route === "string" ? payload.route : "",
    instructions: typeof payload.instructions === "string" ? payload.instructions : "",
    free_text: typeof payload.free_text === "string" ? payload.free_text : "",
  };
};

export const getStandardTemplateDuplicateConflict = ({
  template,
  currentItems,
  excludeIndex,
}: {
  template: PrescriptionTemplateWithItems;
  currentItems: PrescriptionItemInput[];
  excludeIndex?: number;
}): StandardTemplateDuplicateConflict | null => {
  const templateItems = mapStandardTemplateItems(template);
  const existing = new Set(
    currentItems
      .filter((_, index) => index !== excludeIndex)
      .map((item) => normalizeName(item.medication_name ?? ""))
      .filter(Boolean),
  );

  const duplicateNames = templateItems
    .filter((item) => existing.has(normalizeName(item.medication_name ?? "")))
    .map((item) => item.medication_name.trim())
    .filter(Boolean)
    .filter((value, index, list) => list.findIndex((entry) => normalizeName(entry) === normalizeName(value)) === index);

  if (duplicateNames.length === 0) return null;

  return {
    templateId: template.id,
    templateTitle: template.title,
    duplicateNames,
  };
};

export const getManipulatedTemplateDuplicateConflict = ({
  template,
  currentComponents,
  excludeIndex,
}: {
  template: PrescriptionTemplateWithItems;
  currentComponents: ManipulatedComponentInput[];
  excludeIndex?: number;
}): ManipulatedTemplateDuplicateConflict | null => {
  const payload = mapManipulatedTemplatePayload(template);
  const existing = new Set(
    currentComponents
      .filter((_, index) => index !== excludeIndex)
      .map((component) => normalizeName(component.name ?? ""))
      .filter(Boolean),
  );

  const duplicateNames = payload.components
    .filter((component) => existing.has(normalizeName(component.name ?? "")))
    .map((component) => component.name.trim())
    .filter(Boolean)
    .filter((value, index, list) => list.findIndex((entry) => normalizeName(entry) === normalizeName(value)) === index);

  if (duplicateNames.length === 0) return null;

  return {
    templateId: template.id,
    templateTitle: template.title,
    duplicateNames,
  };
};

export const filterUniqueStandardTemplateItems = (
  template: PrescriptionTemplateWithItems,
  currentItems: PrescriptionItemInput[],
  excludeIndex?: number,
): PrescriptionItemInput[] => {
  const existing = new Set(
    currentItems
      .filter((_, index) => index !== excludeIndex)
      .map((item) => normalizeName(item.medication_name ?? ""))
      .filter(Boolean),
  );

  return mapStandardTemplateItems(template).filter(
    (item) => !existing.has(normalizeName(item.medication_name ?? "")),
  );
};

export const filterUniqueManipulatedComponents = (
  template: PrescriptionTemplateWithItems,
  currentComponents: ManipulatedComponentInput[],
  excludeIndex?: number,
): ManipulatedComponentInput[] => {
  const existing = new Set(
    currentComponents
      .filter((_, index) => index !== excludeIndex)
      .map((component) => normalizeName(component.name ?? ""))
      .filter(Boolean),
  );

  return mapManipulatedTemplatePayload(template).components.filter(
    (component) => !existing.has(normalizeName(component.name ?? "")),
  );
};
